import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useMediaQuery } from '../../hooks/mediaQuery';

interface Post {
  display_name: string;
  user_name: string;
  icon: string | null;
  description: string;
  image: string;
  href_link: string;
}

interface CarouselProps {
  instagram_posts: Post[];
  linked_in_posts: Post[];
}

const getImageUrl = (imageUrl: string | null) => {
  if (!imageUrl) return '/default-image.jpg';

  if (!imageUrl.startsWith('http') && !imageUrl.startsWith('https')) {
    return `/${imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl}`;
  }

  return imageUrl;
};

const Carousel: React.FC<CarouselProps> = ({
  instagram_posts,
  linked_in_posts
}) => {
  const isDesktop = useMediaQuery(1280);

  return (
    <>
      <div className="mt-[2rem] overflow-hidden bg-primary w-full h-[550px] md:h-[780px] justify-center gap-0 md:gap-[2rem] text-white flex flex-col md:flex-row  py-auto md:pb-0">
        <div className="w-[30%] hidden h-full xl:block flex justify-end items-center">
          <div className="w-full xl:w-[80%] flex flex-col justify-center items-center h-full text-center ml-auto mr-0">
            <h2 className="text-[28px] md:text-[32px] xl:text-[36px] font-extrabold m-1 xl:m-2">
              Follow us on Social Media
            </h2>
            <p className="text-[20px] xl:text-[28px] xl:text-[20px] m-1 xl:m-2">
              Follow us to explore how modern travelers experience the world and
              shape the future of travel.
            </p>
            <button
              className="text-[16px] bg-white text-primary xl:text-[24px] rounded p-2 m-1 mt-4 md:mt-0 xl:m-2 font-bold px-4"
              onClick={() =>
                window.open(
                  'https://www.linkedin.com/company/seesighttours/',
                  '_blank'
                )
              }
            >
              Connect with us
              {isDesktop ? '' : ' on LinkedIn'}
            </button>
          </div>
        </div>
        <div className="w-full h-full md:w-[60%] flex flex-col md:flex-row justify-center gap-6 xl:gap-8">
          <div className="gap-4 carousel-container">
            <div className="w-full md:w-1/2 h-1/2 md:h-full flex md:flex-col flex-row gap-6 animate-moveLeft md:animate-moveUp">
              {instagram_posts.concat(instagram_posts).map((post, index) => (
                <Link
                  href={post.href_link}
                  key={index}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="bg-white w-[480px]  h-[240px] md:w-[350px] md:h-[450px] rounded-[2.5rem] flex md:flex-col flex-row flex-shrink-0">
                    <div className="w-[48%] h-full bg-blue-100 md:w-full md:h-[55%] rounded-l-[2.5rem] md:rounded-b-none md:rounded-t-[2.5rem]">
                      <Image
                        src={getImageUrl(post.image)}
                        alt="Instagram post"
                        width={1920}
                        height={1080}
                        className="object-cover w-full h-full rounded-l-[2.5rem] md:rounded-b-none md:rounded-t-[2.5rem]"
                      />
                    </div>
                    <div className="w-[52%] h-full md:w-full md:h-[45%] p-3 md:p-3 xl:p-4">
                      <div className="grid grid-cols-5 grid-rows-9 w-full">
                        <div className="col-span-1 row-span-2 rounded-full w-[40px] mt-1 h-[40px] justify-self-center">
                          <Image
                            src="/Instgram-Icon.svg"
                            alt="instagram Icon"
                            width={800}
                            height={820}
                            className="object-contain w-full h-full rounded-full"
                          />
                        </div>
                        <div className="col-span-4 text-primary bottom-0 col-start-2 pl-2 md:pl-1">
                          {post.display_name}
                        </div>
                        <div className="col-span-4 text-primary top-0 col-start-2 pl-1 row-start-2">
                          {post.user_name}
                        </div>
                        <div
                          className="col-span-5 text-primary text-[14px] row-span-8 px-1 py-4 md:py-2 xl:px-4 xl:py-6 row-start-3  text-ellipsis"
                          style={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 8,
                            maxHeight: '12em',
                            overflow: 'hidden'
                          }}
                        >
                          {post.description}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="gap-4 carousel-container">
            <div className="w-full md:w-1/2 h-1/2 md:h-full flex md:flex-col flex-row gap-6 animate-moveRight md:animate-moveDown">
              {linked_in_posts.concat(linked_in_posts).map((post, index) => (
                <Link
                  href={post.href_link}
                  key={index}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="bg-white w-[480px]  h-[240px] md:w-[350px] md:h-[450px] rounded-[2.5rem] flex md:flex-col flex-row flex-shrink-0">
                    <div className="w-[48%] h-full bg-blue-100 md:w-full md:h-[55%] rounded-l-[2.5rem] md:rounded-b-none md:rounded-t-[2.5rem]">
                      <Image
                        src={getImageUrl(post.image)}
                        alt="LinkedIn post"
                        width={1920}
                        height={1080}
                        className="object-cover w-full h-full rounded-l-[2.5rem] md:rounded-b-none md:rounded-t-[2.5rem]"
                      />
                    </div>
                    <div className="w-[52%] h-full md:w-full md:h-[45%] p-3 md:p-3 xl:p-4">
                      <div className="grid grid-cols-5 grid-rows-9 w-full">
                        <div className="col-span-1 row-span-2 rounded-full w-[40px] mt-1 h-[40px] justify-self-center">
                          <Image
                            src="/Linkedin-Icon.svg"
                            alt="LinkedIn Icon"
                            width={800}
                            height={820}
                            className="object-contain w-full h-full rounded-full"
                          />
                        </div>
                        <div className="col-span-4 text-primary bottom-0 col-start-2 pl-2 md:pl-1">
                          {post.display_name}
                        </div>
                        <div className="col-span-4 text-primary top-0 col-start-2 pl-1 row-start-2">
                          {post.user_name}
                        </div>
                        <div
                          className="col-span-5 text-primary text-[14px] row-span-8 px-1 py-4 md:py-2 xl:px-4 xl:py-6 row-start-3  text-ellipsis"
                          style={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 8,
                            maxHeight: '12em',
                            overflow: 'hidden'
                          }}
                        >
                          {post.description}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Carousel;
